// src/components/AdminDashboard.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection,
  getDocs,
  getDoc,
  query,
  where,
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore, functions } from '../firebase';
import { getFuryRegistrationIdToken } from '../utils/furyIdentityToken';
import { httpsCallable } from 'firebase/functions';
import { getDisplayName, getFreshlingDisplayName } from '../utils/flyerDisplay';
import { getEventOperationalStartDate, getEventDate } from '../utils/eventUtils';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { getEventEditionYear } from '../utils/eventRouting';
import {
  getFlyerCreatedAtMs,
  buildCurveFromFlyers,
  interpolateCumulativeAtD,
  medianHistoricCountAtD,
  daysBeforeEventDay,
  toLocalDayStart,
} from '../utils/registrationFillCurve';
import {
  sumSavedInventoryOrdered,
  countFirestoreTshirtUnallocatedRosterMembers,
} from '../utils/tshirtRosterEligibility';
import { computeFreshlingPipelineSummary } from '../utils/freshlingPipeline';
import { computeMentorPipelineSummary } from '../utils/mentorEligibility';
import { useModalEscapeKey } from '../utils/useModalEscapeKey';
import { useAdminPermissions } from '../contexts/AdminPermissionsContext';
import {
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
} from 'recharts';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import './admin/FmModal.css';
import './AdminDashboard.css';

const LOW_JUMPS_THRESHOLD = 25;
const WEEKS_OUT_FOR_LOW_JUMPS_ALERT = 3;

const US_GEO_URL = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';

/** Saturated strokes so each historic year reads clearly in the legend and on the chart. */
const REG_FILL_HISTORIC_STROKES = [
  '#2563eb',
  '#dc2626',
  '#ca8a04',
  '#16a34a',
  '#9333ea',
  '#db2777',
  '#0891b2',
  '#ea580c',
  '#4f46e5',
  '#059669',
  '#b91c1c',
  '#0f766e',
];

/** Calendar date d days before event Saturday (local), shown on fill chart axis. */
function calendarDayBeforeEventSaturday(eventDay, dBefore) {
  if (!eventDay || !Number.isFinite(dBefore)) return null;
  const d = new Date(eventDay.getFullYear(), eventDay.getMonth(), eventDay.getDate());
  d.setDate(d.getDate() - Math.round(dBefore));
  return d;
}

function getFillCurveEditionYear(ev) {
  const raw = ev?.editionYear;
  if (raw != null && raw !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  const dt = getEventDate(ev);
  if (dt && !Number.isNaN(dt.getTime())) return dt.getFullYear();
  return null;
}

/** Year-only label for chart legend / toggles (matches imported-by-year rows). */
function fillCurveDisplayYear(entry) {
  if (String(entry?.id || '').startsWith('__fillYear__')) {
    return entry.title || String(entry.id).replace(/^__fillYear__/, '');
  }
  const y = getFillCurveEditionYear(entry);
  return y != null ? String(y) : String(entry.title || entry.id);
}

function RegistrationFillXAxisTick({ x, y, payload, eventDay, dark }) {
  const dVal = payload?.value ?? payload;
  const cd = calendarDayBeforeEventSaturday(eventDay, dVal);
  const dateStr =
    cd != null ? `${cd.getMonth() + 1}/${cd.getDate()}` : '';
  const fill = dark ? '#94a3b8' : '#64748b';
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill={fill} fontSize={10}>
        {dateStr ? (
          <>
            <tspan x={0} dy={0}>
              {dateStr}
            </tspan>
            <tspan x={0} dy={13}>
              {dVal}d
            </tspan>
          </>
        ) : (
          <tspan x={0} dy={0}>
            {dVal}d
          </tspan>
        )}
      </text>
    </g>
  );
}

const FIPS_TO_STATE = {
  '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT',
  '10':'DE','11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL',
  '18':'IN','19':'IA','20':'KS','21':'KY','22':'LA','23':'ME','24':'MD',
  '25':'MA','26':'MI','27':'MN','28':'MS','29':'MO','30':'MT','31':'NE',
  '32':'NV','33':'NH','34':'NJ','35':'NM','36':'NY','37':'NC','38':'ND',
  '39':'OH','40':'OK','41':'OR','42':'PA','44':'RI','45':'SC','46':'SD',
  '47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA','54':'WV',
  '55':'WI','56':'WY',
};

function isEventThreeWeeksOut(event) {
  const eventDate = getEventOperationalStartDate(event);
  if (!eventDate) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntilEvent = Math.round((eventDate - today) / msPerDay);
  return daysUntilEvent >= 0 && daysUntilEvent <= WEEKS_OUT_FOR_LOW_JUMPS_ALERT * 7;
}

function getJumpCount(flyer) {
  const raw = flyer?.jumpNumber;
  if (raw == null || raw === '') return null;
  const n = parseInt(String(raw).trim(), 10);
  return Number.isFinite(n) ? n : null;
}

function FreshlingsPie({ waiting, paired }) {
  const total = waiting + paired;
  const size = 44;
  const r = 14;
  const cx = size / 2;
  const cy = size / 2;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const angleToXY = (angleDeg) => ({
    x: cx + r * Math.cos(toRad(-90 + angleDeg)),
    y: cy + r * Math.sin(toRad(-90 + angleDeg)),
  });
  if (total === 0) {
    return (
      <svg className="admin-dashboard-pie" viewBox={`0 0 ${size} ${size}`} aria-hidden>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth="6" />
      </svg>
    );
  }
  const waitingFrac = waiting / total;
  const pairedFrac = paired / total;
  const start = angleToXY(0);
  const waitingEndAngle = waitingFrac * 360;
  const waitingEnd = angleToXY(waitingEndAngle);
  const pathWaiting =
    waitingFrac <= 0
      ? null
      : `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${waitingFrac > 0.5 ? 1 : 0} 1 ${waitingEnd.x} ${waitingEnd.y} Z`;
  const pathPaired =
    pairedFrac <= 0
      ? null
      : `M ${cx} ${cy} L ${waitingEnd.x} ${waitingEnd.y} A ${r} ${r} 0 ${pairedFrac > 0.5 ? 1 : 0} 1 ${start.x} ${start.y} Z`;
  return (
    <svg className="admin-dashboard-pie" viewBox={`0 0 ${size} ${size}`} aria-hidden>
      {pathWaiting && <path d={pathWaiting} fill="#f07814" />}
      {pathPaired && <path d={pathPaired} fill="#3c3c3c" />}
    </svg>
  );
}

function isWithdrawnOrDuplicate(flyer) {
  const statusText = (flyer.status || '').toString().toLowerCase();
  const notesText = (flyer.notes || '').toString().toLowerCase();
  return (
    statusText.includes('withdrawn') ||
    statusText.includes('duplicate') ||
    statusText.includes('cancelled') ||
    statusText.includes('canceled') ||
    notesText.includes('withdrawn') ||
    notesText.includes('duplicate') ||
    notesText.includes('cancelled') ||
    notesText.includes('canceled')
  );
}

const DAY_COLS = ['wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_HEADERS = ['Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function getDaysUntilEvent(event) {
  const eventDate = getEventOperationalStartDate(event);
  if (!eventDate) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysUntilEvent = Math.round((eventDate - today) / msPerDay);
  return daysUntilEvent;
}

function AlertItem({ issue, ack, onMarkOk, onMarkEntityOk, navigate }) {
  const hasEntities = issue.entityIds && issue.entityIds.length > 0;
  const entityAcks = hasEntities && ack?.entities ? ack.entities : {};
  const wholeAck = !hasEntities && ack?.ok;
  const allEntitiesOk = hasEntities && issue.entityIds.every((id) => entityAcks[id]?.ok);
  const isAllOk = wholeAck || allEntitiesOk;
  const [expanded, setExpanded] = useState(false);
  const [noteInput, setNoteInput] = useState('');
  const [entityNoteInputs, setEntityNoteInputs] = useState({});
  const [saving, setSaving] = useState(false);
  const summaryNote = wholeAck ? ack.note : (allEntitiesOk && ack?.allNote ? ack.allNote : null);

  const handleMarkWholeOk = async () => {
    setSaving(true);
    try {
      await onMarkOk(issue, noteInput.trim());
      setNoteInput('');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkEntityOk = async (entityId) => {
    setSaving(true);
    try {
      const note = entityNoteInputs[entityId] || '';
      await onMarkEntityOk(issue, entityId, note.trim());
      setEntityNoteInputs((p) => ({ ...p, [entityId]: '' }));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={`admin-dashboard-alert admin-dashboard-alert--${issue.severity} ${isAllOk ? 'admin-dashboard-alert--acked' : ''}`}>
      <button
        type="button"
        className="admin-dashboard-alert-toggle"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        title={isAllOk ? 'Expand to see details' : 'Expand'}
      >
        <span className="admin-dashboard-alert-icon">
          {isAllOk ? '✓' : issue.severity === 'warning' ? '⚠' : 'ℹ'}
        </span>
        <span className="admin-dashboard-alert-message">
          {issue.message}
          {isAllOk && summaryNote && (
            <span className="admin-dashboard-alert-acked-note"> — {summaryNote}</span>
          )}
        </span>
        <span className="admin-dashboard-alert-expand-icon">{expanded ? '▼' : '▶'}</span>
      </button>
      {expanded && (
        <div className="admin-dashboard-alert-detail">
          {hasEntities ? (
            <ul className="admin-dashboard-alert-entity-list">
              {issue.entityIds.map((entityId) => {
                const label = issue.entityLabels?.[entityId] || entityId;
                const entityAck = entityAcks[entityId];
                return (
                  <li key={entityId} className={entityAck?.ok ? 'admin-dashboard-alert-entity--ok' : ''}>
                    <span className="admin-dashboard-alert-entity-label">{label}</span>
                    {entityAck?.ok ? (
                      <span className="admin-dashboard-alert-entity-status">
                        ✓ {entityAck.note || 'Marked ok'}
                      </span>
                    ) : (
                      <div className="admin-dashboard-alert-entity-actions">
                        <input
                          type="text"
                          placeholder="Note (optional)"
                          value={entityNoteInputs[entityId] || ''}
                          onChange={(e) => setEntityNoteInputs((p) => ({ ...p, [entityId]: e.target.value }))}
                          className="admin-dashboard-alert-note-input"
                        />
                        <button
                          type="button"
                          className="admin-dashboard-alert-mark-ok"
                          onClick={() => handleMarkEntityOk(entityId)}
                          disabled={saving}
                        >
                          Mark ok
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="admin-dashboard-alert-whole-actions">
              {!wholeAck && (
                <>
                  <input
                    type="text"
                    placeholder="Note (optional)"
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    className="admin-dashboard-alert-note-input"
                  />
                  <button
                    type="button"
                    className="admin-dashboard-alert-mark-ok"
                    onClick={handleMarkWholeOk}
                    disabled={saving}
                  >
                    Mark ok
                  </button>
                </>
              )}
            </div>
          )}
          <button
            type="button"
            className="admin-dashboard-alert-action"
            onClick={() => navigate(issue.link)}
          >
            {issue.linkLabel}
          </button>
        </div>
      )}
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlEventParam = useMemo(
    () => (searchParams.get('event') || searchParams.get('eventId') || '').toString().trim(),
    [searchParams]
  );
  const { canAccessRoute, loading: adminPermsLoading } = useAdminPermissions();
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [flyers, setFlyers] = useState([]);
  const [tshirtInventoryOrderedSum, setTshirtInventoryOrderedSum] = useState(0);
  const [allFlyers, setAllFlyers] = useState([]);
  const [acknowledgments, setAcknowledgments] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [importingAllEventId, setImportingAllEventId] = useState(null);
  const [importAllStatus, setImportAllStatus] = useState(null);
  const [importAllConfirmOpen, setImportAllConfirmOpen] = useState(false);
  const [dzMappings, setDzMappings] = useState({});
  const [showAllIssues, setShowAllIssues] = useState(false);
  const [showAllIssueEvents, setShowAllIssueEvents] = useState(false);
  const [showSubEventDetails, setShowSubEventDetails] = useState(false);
  const [showGeoDetails, setShowGeoDetails] = useState(false);
  const [fillHistVisibleById, setFillHistVisibleById] = useState({});
  const [registrationFillByYear, setRegistrationFillByYear] = useState({});
  const [configActiveEventId, setConfigActiveEventId] = useState('');

  const resolvedDashboardEventId = useMemo(
    () => resolveAdminDefaultEventId({ eventIdParam: urlEventParam, activeEventId: configActiveEventId, events }),
    [urlEventParam, configActiveEventId, events]
  );

  const onDashboardEventChange = useCallback(
    (nextId) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.delete('eventId');
        const id = (nextId || '').toString().trim();
        if (id) p.set('event', id);
        else p.delete('event');
        return p;
      });
      setShowAllIssues(false);
    },
    [setSearchParams]
  );

  const activeEvent = useMemo(() => {
    if (!events.length) return null;
    const hit = events.find((e) => e.id === resolvedDashboardEventId);
    return hit || events[0] || null;
  }, [events, resolvedDashboardEventId]);

  useModalEscapeKey(importAllConfirmOpen, () => setImportAllConfirmOpen(false));

  const fetchEvents = React.useCallback(async () => {
    try {
      const [snap, activeId] = await Promise.all([
        getDocs(collection(firestore, 'events')),
        getActiveEventId(),
      ]);
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setConfigActiveEventId((activeId || '').toString().trim());
    } catch (err) {
      setError('Failed to fetch events: ' + err.message);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fetchAllFlyers = React.useCallback(async () => {
    try {
      const snap = await getDocs(collection(firestore, 'flyers'));
      setAllFlyers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Failed to fetch all flyers:', err);
    }
  }, []);

  useEffect(() => {
    fetchAllFlyers().catch(() => {});
  }, [fetchAllFlyers]);

  // Refetch when window gains focus: flyers, event docs (e.g. registrationFillCurve after Rebuild), and byYear
  useEffect(() => {
    const onFocus = () => {
      fetchAllFlyers();
      fetchEvents();
      getDoc(doc(firestore, '_config', 'registrationFillCurves'))
        .then((snap) => setRegistrationFillByYear(snap.exists() ? snap.data().byYear || {} : {}))
        .catch(() => {});
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchAllFlyers, fetchEvents]);

  useEffect(() => {
    let cancelled = false;
    async function fetchAcknowledgments() {
      try {
        const snap = await getDocs(collection(firestore, 'alertAcknowledgments'));
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => {
          map[d.id] = d.data();
        });
        setAcknowledgments(map);
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch acknowledgments:', err);
      }
    }
    fetchAcknowledgments();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, '_config', 'dzMappings'));
        if (!cancelled) setDzMappings(snap.exists() ? (snap.data().mappings || {}) : {});
      } catch {
        if (!cancelled) setDzMappings({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, '_config', 'registrationFillCurves'));
        if (cancelled) return;
        setRegistrationFillByYear(snap.exists() ? snap.data().byYear || {} : {});
      } catch {
        if (!cancelled) setRegistrationFillByYear({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadEventRosterData = useCallback(async (eventId) => {
    const [teamsSnap, flyersSnap, invSnap] = await Promise.all([
      getDocs(query(collection(firestore, 'teams'), where('eventId', '==', eventId))),
      getDocs(query(collection(firestore, 'flyers'), where('eventId', '==', eventId))),
      getDoc(doc(firestore, 'tshirtInventory', eventId)),
    ]);
    setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setFlyers(flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    const sizes = invSnap.exists() ? invSnap.data().sizes : null;
    setTshirtInventoryOrderedSum(sumSavedInventoryOrdered(sizes));
  }, []);

  useEffect(() => {
    if (!activeEvent?.id) {
      setTeams([]);
      setFlyers([]);
      setTshirtInventoryOrderedSum(0);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadEventRosterData(activeEvent.id)
      .then(() => {
        if (cancelled) return;
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Failed to load data');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeEvent?.id, loadEventRosterData]);

  const handleImportAll = async (ev) => {
    setImportingAllEventId(ev.id);
    setError('');
    setImportAllStatus(null);
    try {
      if (ev.registrationImportSource === 'fury_api') {
        const furyId = (ev.furyRegistrationEventId || '').trim();
        if (!furyId) {
          const message = 'Set Fury Registration event ID in Event Management before importing.';
          setError(message);
          setImportAllStatus({ eventId: ev.id, type: 'error', message });
          return;
        }
        const tokenResult = await getFuryRegistrationIdToken();
        if (!tokenResult.ok) {
          setError(tokenResult.error);
          setImportAllStatus({ eventId: ev.id, type: 'error', message: tokenResult.error });
          return;
        }
        const syncFury = httpsCallable(functions, 'syncRegistrationsFromFuryApi');
        await syncFury({ eventId: ev.id, idToken: tokenResult.idToken });
      } else {
        if (!ev?.freshlingsSpreadsheetId) {
          const message = 'Link a Google Sheet to this event first (Edit event in Event Management).';
          setError(message);
          setImportAllStatus({ eventId: ev.id, type: 'error', message });
          return;
        }
        const importFreshlings = httpsCallable(functions, 'importFreshlingsFromSheet');
        const importMentors = httpsCallable(functions, 'importMentorsFromSheet');
        const importEventSummary = httpsCallable(functions, 'importEventSummaryFromSheet');
        await importFreshlings({ eventId: ev.id });
        await importMentors({ eventId: ev.id });
        await importEventSummary({ eventId: ev.id });
      }
      const snap = await getDocs(collection(firestore, 'events'));
      setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      await loadEventRosterData(ev.id);
      const flyerSnap = await getDocs(
        query(collection(firestore, 'flyers'), where('eventId', '==', ev.id))
      );
      const flyerListForSummary = flyerSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const msum = computeMentorPipelineSummary(flyerListForSummary);
      const fsum = computeFreshlingPipelineSummary(flyerListForSummary);
      const mentorMsg =
        `Mentors after import: ${msum.pendingStaff} pending staff, ${msum.approvedReg} approved, ` +
        `${msum.fmExcluded} FM-excluded from pairing, ${msum.waitingList} waiting list, ` +
        `${msum.partialDenied} partial denial, ${msum.cancelled} off roster ` +
        `(${msum.total} mentor flyers).`;
      const freshlingMsg =
        `Freshlings after import: ${fsum.active} active, ${fsum.waitingList} waiting list, ` +
        `${fsum.cancelled} cancelled, ${fsum.withdrawnOrDuplicate} withdrawn or duplicate ` +
        `(${fsum.total} freshling flyers).`;
      setImportAllStatus({
        eventId: ev.id,
        type: 'success',
        message: `Import complete. Registrations and dashboard data refreshed. ${mentorMsg} ${freshlingMsg}`,
        mentorPipelineSummary: msum,
        freshlingPipelineSummary: fsum,
      });
    } catch (err) {
      const message = err.message || 'Import all failed.';
      setError(message);
      setImportAllStatus({ eventId: ev.id, type: 'error', message });
    } finally {
      setImportingAllEventId(null);
    }
  };

  const mentors = useMemo(() => flyers.filter((f) => f.type === 'mentor'), [flyers]);
  const freshlings = useMemo(() => flyers.filter((f) => f.type === 'freshling'), [flyers]);

  const tshirtFirestoreUnallocatedCount = useMemo(
    () => countFirestoreTshirtUnallocatedRosterMembers(activeEvent, flyers),
    [activeEvent, flyers]
  );

  const showTshirtDashboardWarning =
    !adminPermsLoading &&
    canAccessRoute('tshirts') &&
    tshirtInventoryOrderedSum > 0 &&
    tshirtFirestoreUnallocatedCount > 0;

  const dzStats = useMemo(() => {
    const activeFreshlings = freshlings.filter((f) => !f.test && !f.cancelled);
    const withDz = activeFreshlings.filter((f) => (f.homeDz || '').trim());
    const lookup = (raw) => dzMappings[(raw || '').trim().toLowerCase()] || null;

    const stateCounts = new Map();
    const dzCounts = new Map();
    withDz.forEach((f) => {
      const raw = (f.homeDz || '').trim();
      const m = lookup(raw);
      const dzName = m ? m.name : raw;
      dzCounts.set(dzName, (dzCounts.get(dzName) || 0) + 1);
      if (m) {
        const stLabel = m.country && m.country !== 'US' ? m.country : (m.state || 'Unknown');
        stateCounts.set(stLabel, (stateCounts.get(stLabel) || 0) + 1);
      }
    });

    const caCount = stateCounts.get('CA') || 0;
    const nonCaUs = Array.from(stateCounts.entries())
      .filter(([k]) => k !== 'CA' && k.length === 2)
      .reduce((s, [, v]) => s + v, 0);
    const intl = Array.from(stateCounts.entries())
      .filter(([k]) => k.length > 2 && k !== 'Unknown')
      .reduce((s, [, v]) => s + v, 0);

    const perris = dzCounts.get('Skydive Perris') || 0;
    const elsinore = dzCounts.get('Skydive Elsinore') || 0;

    const stateByAbbr = {};
    stateCounts.forEach((count, label) => {
      if (label.length === 2) stateByAbbr[label] = count;
    });
    const maxStateCount = Math.max(1, ...Object.values(stateByAbbr));

    const stateRows = Array.from(stateCounts.entries())
      .map(([label, count]) => ({ label, count, pct: withDz.length ? ((count / withDz.length) * 100).toFixed(1) : '0.0' }))
      .sort((a, b) => b.count - a.count);

    return {
      total: activeFreshlings.length,
      withDz: withDz.length,
      caCount, nonCaUs, intl, perris, elsinore,
      stateByAbbr, maxStateCount, stateRows,
    };
  }, [freshlings, dzMappings]);

  // Same logic as Team Pairing: "No team" = active non-test freshlings not on any non-test team
  const assignedFreshlingEmails = useMemo(() => {
    const set = new Set();
    teams.filter((t) => !t.test).forEach((t) => {
      (t.freshlings || []).forEach((e) => set.add((e || '').toLowerCase()));
    });
    return set;
  }, [teams]);
  const computedFreshlingsWithNoTeam = useMemo(() => {
    if (!activeEvent?.id) return null;
    return freshlings.filter(
      (f) =>
        !f.test &&
        !f.cancelled &&
        !isWithdrawnOrDuplicate(f) &&
        !assignedFreshlingEmails.has((f.email || '').toLowerCase())
    ).length;
  }, [activeEvent?.id, freshlings, assignedFreshlingEmails]);

  // Recent signup activity from createdAt: last 24h overall, last 7d per sub-event (manifestTickets label)
  const signupActivityByEventId = useMemo(() => {
    const result = {};
    if (!allFlyers.length) return result;
    const nowMs = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const cutoff24 = nowMs - dayMs;
    const cutoff7 = nowMs - 7 * dayMs;

    allFlyers.forEach((f) => {
      if (!f || f.type !== 'freshling' || f.test || f.cancelled || isWithdrawnOrDuplicate(f) || !f.eventId) return;
      const createdMs = getFlyerCreatedAtMs(f);
      if (createdMs == null) return;
      const in24 = createdMs >= cutoff24;
      const in7 = createdMs >= cutoff7;
      if (!in24 && !in7) return;
      const evId = f.eventId;
      if (!result[evId]) {
        result[evId] = {
          last24All: 0,
          last7ByCategory: {},
        };
      }
      if (in24) {
        result[evId].last24All += 1;
      }
      if (in7) {
        const tickets = f.manifestTickets || {};
        Object.entries(tickets).forEach(([label, val]) => {
          if (!label || Number(val) <= 0) return;
          result[evId].last7ByCategory[label] =
            (result[evId].last7ByCategory[label] || 0) + 1;
        });
      }
    });

    return result;
  }, [allFlyers]);

  const historicFillCurveEvents = useMemo(() => {
    if (!activeEvent?.id) return [];
    const fromEvents = events.filter(
      (e) =>
        e.id !== activeEvent.id &&
        Array.isArray(e.registrationFillCurve?.points) &&
        e.registrationFillCurve.points.length > 0
    );
    const fromConfig = Object.entries(registrationFillByYear || {})
      .filter(([, curve]) => Array.isArray(curve?.points) && curve.points.length > 0)
      .map(([y, curve]) => ({
        id: `__fillYear__${y}`,
        title: String(y),
        date: `${y}-12-31`,
        registrationFillCurve: curve,
      }));
    return [...fromEvents, ...fromConfig]
      .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')))
      .slice(0, 20);
  }, [events, activeEvent?.id, registrationFillByYear]);

  const historicFillStrokeById = useMemo(() => {
    const m = new Map();
    historicFillCurveEvents.forEach((e, i) => {
      m.set(e.id, REG_FILL_HISTORIC_STROKES[i % REG_FILL_HISTORIC_STROKES.length]);
    });
    return m;
  }, [historicFillCurveEvents]);

  useEffect(() => {
    setFillHistVisibleById((prev) => {
      const next = { ...prev };
      historicFillCurveEvents.forEach((e) => {
        if (next[e.id] === undefined) next[e.id] = true;
      });
      Object.keys(next).forEach((id) => {
        if (!historicFillCurveEvents.some((ev) => ev.id === id)) delete next[id];
      });
      return next;
    });
  }, [historicFillCurveEvents]);

  const registrationFillChart = useMemo(() => {
    if (!activeEvent) return null;

    const curveFinalC = (curve) => {
      const fc = curve?.finalC;
      if (Number.isFinite(fc) && fc > 0) return fc;
      const pts = curve?.points;
      if (pts && pts.length) return Number(pts[pts.length - 1].c) || 0;
      return 0;
    };

    const liveFlyers = allFlyers.filter((f) => f.eventId === activeEvent.id);
    const liveBuilt = buildCurveFromFlyers(liveFlyers, activeEvent);
    const livePoints = liveBuilt.points;
    const liveFinal = curveFinalC({ points: livePoints, finalC: liveBuilt.finalC });
    const liveFinalNorm = Math.max(1, liveFinal);

    const historicsShown = historicFillCurveEvents.filter((e) => fillHistVisibleById[e.id] !== false);
    const historicCurves = historicsShown
      .map((e) => e.registrationFillCurve)
      .filter((c) => c && Array.isArray(c.points) && c.points.length && curveFinalC(c) > 0);

    let maxD = 0;
    if (livePoints.length) maxD = Math.max(maxD, livePoints[0].d);
    historicsShown.forEach((e) => {
      const pts = e.registrationFillCurve?.points;
      if (pts && pts.length) maxD = Math.max(maxD, pts[0].d);
    });
    if (maxD === 0) maxD = 120;

    const chartRowsFull = [];
    for (let d = maxD; d >= 0; d -= 1) {
      const row = { d };
      historicsShown.forEach((e) => {
        const curve = e.registrationFillCurve;
        const fc = curveFinalC(curve);
        if (fc <= 0) return;
        const cnt = interpolateCumulativeAtD(curve.points, fc, d);
        row[`h_${e.id}`] = cnt != null ? Math.round(cnt) : null;
      });
      const med =
        historicCurves.length > 0 ? medianHistoricCountAtD(historicCurves, d) : null;
      row.medianCount = med != null ? Math.round(med) : null;
      const curCount =
        livePoints.length && liveFinalNorm > 0
          ? interpolateCumulativeAtD(livePoints, liveFinalNorm, d)
          : null;
      row.currentCount = curCount != null ? Math.round(curCount) : null;
      chartRowsFull.push(row);
    }

    const countShowsActivity = (v) => v != null && Number(v) > 0;
    const rowHasActivity = (row) => {
      if (countShowsActivity(row.currentCount) || countShowsActivity(row.medianCount)) return true;
      return historicsShown.some((e) => countShowsActivity(row[`h_${e.id}`]));
    };

    let dFirstWithActivity = null;
    for (const row of chartRowsFull) {
      if (rowHasActivity(row)) {
        dFirstWithActivity = row.d;
        break;
      }
    }

    const PAD_DAYS = 4;
    const dAxisHigh =
      dFirstWithActivity != null ? Math.min(maxD, dFirstWithActivity + PAD_DAYS) : maxD;
    const chartRows =
      dFirstWithActivity != null
        ? chartRowsFull.filter((r) => r.d <= dAxisHigh)
        : chartRowsFull;

    const eventDay = getEventDate(activeEvent);
    const todayStart = toLocalDayStart(new Date());
    const dToday =
      eventDay && todayStart ? daysBeforeEventDay(todayStart, eventDay) : null;

    let paceLabel = null;
    let paceDelta = null;
    if (
      dToday != null &&
      dToday >= 0 &&
      livePoints.length > 0 &&
      historicCurves.length > 0 &&
      liveFinalNorm > 0
    ) {
      const cur = interpolateCumulativeAtD(livePoints, liveFinalNorm, dToday);
      const med = medianHistoricCountAtD(historicCurves, dToday);
      if (cur != null && med != null) {
        paceDelta = Math.round(cur - med);
        const th = Math.max(5, Math.round(med * 0.08));
        if (paceDelta > th) paceLabel = 'Ahead of typical pace';
        else if (paceDelta < -th) paceLabel = 'Behind typical pace';
        else paceLabel = 'On track';
      }
    } else if (dToday != null && dToday < 0) {
      paceLabel = 'Event date has passed (curve still useful for archives)';
    }

    const showSection =
      chartRows.length > 0 &&
      (livePoints.length > 0 || historicsShown.length > 0);

    return {
      chartRows,
      historicsShown,
      liveFinal,
      dToday,
      paceLabel,
      paceDelta,
      showSection,
    };
  }, [activeEvent, allFlyers, historicFillCurveEvents, fillHistVisibleById]);

  const issues = useMemo(() => {
    const list = [];
    for (const ev of events) {
      const s = ev.eventSummary;
      const freshlingsWithNoTeam = s?.freshlingsWithNoTeam ?? s?.freshlingsWaitingTeamAssignment ?? 0;
      const mentorsAccepted = s?.mentorsAccepted ?? 0;
      const requiredMentors = Math.ceil((freshlingsWithNoTeam || 0) / 2);
      const mentorGap = Math.max(0, requiredMentors - (mentorsAccepted || 0));
      if (mentorGap > 0) {
        list.push({
          id: `mentor-${ev.id}`,
          alertType: 'mentor-shortfall',
          eventId: ev.id,
          severity: 'warning',
          message: `${ev.title}: Need ${mentorGap} more mentors`,
          link: `/admin/team-pairing?eventId=${encodeURIComponent(ev.id)}`,
          linkLabel: 'TEAM PAIRING',
        });
      }
      const activeRow = s?.byDayTable?.find((r) => r.label === 'Active Registrations');
      const activeTotal = activeRow?.total ?? null;
      const maxFreshlings = ev.maxFreshlings != null ? ev.maxFreshlings : 112;
      if (activeTotal != null && maxFreshlings > 0) {
        const pct = Math.round((activeTotal / maxFreshlings) * 100);
        if (pct >= 100) {
          list.push({
            id: `capacity-${ev.id}`,
            alertType: 'capacity',
            eventId: ev.id,
            severity: 'info',
            message: `${ev.title}: At ${pct}% capacity`,
            link: `/admin/events?expand=${encodeURIComponent(ev.id)}`,
            linkLabel: 'EVENTS',
          });
        }
      }
      const addOnsNoReg = s?.emailsWithAddOnsButNoRegistration;
      if (Array.isArray(addOnsNoReg) && addOnsNoReg.length > 0) {
        list.push({
          id: `addons-no-registration-${ev.id}`,
          alertType: 'addons-no-registration',
          eventId: ev.id,
          severity: 'warning',
          message: `${ev.title}: ${addOnsNoReg.length} people with add-ons in manifest but not registered for meet`,
          link: `/admin/events?expand=${encodeURIComponent(ev.id)}`,
          linkLabel: 'EVENT SUMMARY',
        });
      }
      const inAppNotInSheet = s?.freshlingsInAppNotInSheet;
      if (Array.isArray(inAppNotInSheet) && inAppNotInSheet.length > 0) {
        list.push({
          id: `freshlings-not-in-sheet-${ev.id}`,
          alertType: 'freshlings-not-in-sheet',
          eventId: ev.id,
          severity: 'info',
          message: `${ev.title}: ${inAppNotInSheet.length} freshling(s) in app but not in sheet (dashboard count includes these)`,
          link: `/admin/flyer-management?eventId=${encodeURIComponent(ev.id)}`,
          linkLabel: 'FLYERS',
        });
      }

      if (isEventThreeWeeksOut(ev)) {
        const eventFlyers = allFlyers.filter(
          (f) =>
            f.eventId === ev.id &&
            f.type === 'freshling' &&
            !f.cancelled &&
            !isWithdrawnOrDuplicate(f)
        );
        const lowJumps = eventFlyers.filter((f) => {
          const jumps = getJumpCount(f);
          return jumps != null && jumps < LOW_JUMPS_THRESHOLD;
        });
        if (lowJumps.length > 0) {
          list.push({
            id: `low-jumps-${ev.id}`,
            alertType: 'low-jumps',
            eventId: ev.id,
            severity: 'warning',
            message: `${ev.title}: ${lowJumps.length} freshling(s) with under ${LOW_JUMPS_THRESHOLD} jumps`,
            link: `/admin/flyer-management?eventId=${encodeURIComponent(ev.id)}`,
            linkLabel: 'FLYERS',
            entityIds: lowJumps.map((f) => f.id),
            entityLabels: Object.fromEntries(lowJumps.map((f) => [f.id, getFreshlingDisplayName(f) || f.email || f.id])),
          });
        }
      }
    }
    if (activeEvent && teams.length > 0) {
      const teamLabel = (t) => {
        const name = (t.teamName || '').toString().trim();
        if (name) return name;
        const parts = [];
        const mentorEmail = (t.mentor || '').toString().trim();
        if (mentorEmail) {
          const mentorFlyer = mentors.find((m) => (m.email || '').toLowerCase() === mentorEmail.toLowerCase());
          parts.push(getDisplayName(mentorFlyer) || mentorEmail);
        }
        (t.freshlings || []).forEach((email) => {
          const f = freshlings.find((fl) => (fl.email || '').toLowerCase() === (email || '').toLowerCase());
          parts.push(getFreshlingDisplayName(f) || email);
        });
        return parts.length ? parts.join(', ') : t.id;
      };

      const teamsWithIssue = teams.filter((t) => (t.notificationStatus || 'pending') === 'issue');
      if (teamsWithIssue.length > 0) {
        const names = teamsWithIssue.map(teamLabel).join(', ');
        list.push({
          id: 'teams-marked-issue',
          alertType: 'teams-marked-issue',
          eventId: activeEvent.id,
          severity: 'warning',
          message: `Teams marked as having an issue: ${names}`,
          link: `/admin/team-pairing?eventId=${encodeURIComponent(activeEvent.id)}`,
          linkLabel: 'TEAM PAIRING',
          entityIds: teamsWithIssue.map((t) => t.id),
          entityLabels: Object.fromEntries(teamsWithIssue.map((t) => [t.id, teamLabel(t)])),
        });
      }

      const teamsMissingFreshling = teams.filter((t) => (t.freshlings || []).length < 2);
      if (teamsMissingFreshling.length > 0) {
        const names = teamsMissingFreshling.map(teamLabel).join(', ');
        list.push({
          id: 'teams-missing-freshling',
          alertType: 'teams-missing-freshling',
          eventId: activeEvent.id,
          severity: 'warning',
          message: `Teams missing a freshling (need 2): ${names}`,
          link: `/admin/team-pairing?eventId=${encodeURIComponent(activeEvent.id)}`,
          linkLabel: 'TEAM PAIRING',
          entityIds: teamsMissingFreshling.map((t) => t.id),
          entityLabels: Object.fromEntries(teamsMissingFreshling.map((t) => [t.id, teamLabel(t)])),
        });
      }

      const teamsMissingMentor = teams.filter((t) => !(t.mentor || '').toString().trim());
      if (teamsMissingMentor.length > 0) {
        const names = teamsMissingMentor.map(teamLabel).join(', ');
        list.push({
          id: 'teams-missing-mentor',
          alertType: 'teams-missing-mentor',
          eventId: activeEvent.id,
          severity: 'warning',
          message: `Teams missing a mentor: ${names}`,
          link: `/admin/team-pairing?eventId=${encodeURIComponent(activeEvent.id)}`,
          linkLabel: 'TEAM PAIRING',
          entityIds: teamsMissingMentor.map((t) => t.id),
          entityLabels: Object.fromEntries(teamsMissingMentor.map((t) => [t.id, teamLabel(t)])),
        });
      }
    }
    return list;
  }, [events, teams, mentors, freshlings, activeEvent, allFlyers]);

  const allIssues = useMemo(() => [...issues], [issues]);

  const scopedIssues = useMemo(() => {
    if (showAllIssueEvents) return allIssues;
    const id = (resolvedDashboardEventId || '').toString().trim();
    if (!id) return [];
    return allIssues.filter((issue) => issue.eventId === id);
  }, [allIssues, showAllIssueEvents, resolvedDashboardEventId]);

  const issueSummary = useMemo(() => {
    const warning = scopedIssues.filter((i) => i.severity === 'warning').length;
    const info = scopedIssues.filter((i) => i.severity === 'info').length;
    return { total: scopedIssues.length, warning, info };
  }, [scopedIssues]);

  const handleMarkAlertOk = async (issue, note) => {
    if (!issue?.eventId || !issue?.alertType) return;
    const ref = doc(firestore, 'alertAcknowledgments', issue.eventId);
    const current = acknowledgments[issue.eventId] || {};
    const stateUpdate = { ...current, [issue.alertType]: { ok: true, note: note || null } };
    await setDoc(ref, { ...stateUpdate, updatedAt: serverTimestamp() }, { merge: true });
    setAcknowledgments((prev) => ({ ...prev, [issue.eventId]: stateUpdate }));
  };

  const handleMarkAlertEntityOk = async (issue, entityId, note) => {
    if (!issue?.eventId || !issue?.alertType) return;
    const ref = doc(firestore, 'alertAcknowledgments', issue.eventId);
    const current = acknowledgments[issue.eventId] || {};
    const typeData = current[issue.alertType] || {};
    const entities = { ...(typeData.entities || {}), [entityId]: { ok: true, note: note || null } };
    const allOk = issue.entityIds.every((id) => (id === entityId ? true : entities[id]?.ok));
    const stateUpdate = {
      ...current,
      [issue.alertType]: {
        ...typeData,
        entities,
        ...(allOk ? { allNote: 'All ok' } : {}),
      },
    };
    await setDoc(ref, { ...stateUpdate, updatedAt: serverTimestamp() }, { merge: true });
    setAcknowledgments((prev) => ({ ...prev, [issue.eventId]: stateUpdate }));
  };

  if (events.length === 0 && !loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-dashboard-hero">
          <p>No events yet. Add an event to get started.</p>
          <button type="button" className="btn-fm-primary" onClick={() => navigate('/admin/events')}>
            Go to Events
          </button>
        </div>
      </div>
    );
  }

  const editionYear = activeEvent ? getEventEditionYear(activeEvent) : null;

  return (
    <div className="admin-dashboard">
      {importAllConfirmOpen && activeEvent && (
        <div
          className="fm-modal-overlay"
          onClick={() => setImportAllConfirmOpen(false)}
          role="presentation"
        >
          <div
            className="fm-modal fm-modal--sm"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-dashboard-import-all-title"
          >
            <div className="fm-modal-header">
              <h2 className="fm-modal-title" id="admin-dashboard-import-all-title">
                Confirm import
              </h2>
              <button
                type="button"
                className="fm-modal-close"
                onClick={() => setImportAllConfirmOpen(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="fm-modal-body">
              <p style={{ margin: 0, lineHeight: 1.5 }}>
                Re-import all data from Fury Registration for {activeEvent.title}? Existing records will be updated.
              </p>
            </div>
            <div className="fm-modal-footer">
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-cancel"
                onClick={() => setImportAllConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fm-modal-btn fm-modal-btn-primary"
                onClick={() => {
                  setImportAllConfirmOpen(false);
                  handleImportAll(activeEvent);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {error && <div className="admin-dashboard-error">{error}</div>}
      {events.length > 0 && (
        <section className="admin-dashboard-section admin-dashboard-public-links" aria-label="Public dashboard links">
          <div className="admin-dashboard-event-picker">
            <label htmlFor="admin-dashboard-event-select">
              Event
              <select
                id="admin-dashboard-event-select"
                value={resolvedDashboardEventId}
                onChange={(e) => onDashboardEventChange(e.target.value)}
              >
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.title || ev.id}
                    {ev.id === configActiveEventId ? ' (active default)' : ''}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <h2 className="admin-dashboard-section-heading">Public team dashboard</h2>
          <p className="admin-dashboard-muted">
            {activeEvent?.id ? (
              <>
                <button
                  type="button"
                  className="admin-dashboard-btn-link"
                  onClick={() =>
                    window.open(`/dashboard?event=${encodeURIComponent(activeEvent.id)}`, '_blank', 'noopener,noreferrer')
                  }
                >
                  Open /dashboard (this default event)
                </button>
                {editionYear != null ? (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="admin-dashboard-btn-link"
                      onClick={() => navigate(`/admin/live/${editionYear}`)}
                    >
                      Admin live view ({editionYear})
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <span>Set a default event in Event Management to link the live dashboard.</span>
            )}
          </p>
        </section>
      )}
      {activeEvent && (() => {
        const s = activeEvent.eventSummary;
        const byDayTable = s?.byDayTable || [];
        const activeRow = byDayTable.find((r) => r.label === 'Active Registrations');
        const subEventRows = byDayTable.filter((r) => r.label !== 'Active Registrations');
        const activeFreshlings = activeRow?.total ?? null;
        const maxFreshlings = activeEvent.maxFreshlings != null ? activeEvent.maxFreshlings : 112;
        const capacityPct =
          activeFreshlings != null && maxFreshlings > 0
            ? Math.min(999, Math.round((activeFreshlings / maxFreshlings) * 100))
            : null;
        const freshlingsWithNoTeam =
          computedFreshlingsWithNoTeam != null
            ? computedFreshlingsWithNoTeam
            : (s?.freshlingsWithNoTeam ?? s?.freshlingsWaitingTeamAssignment ?? 0);
        const freshlingsPaired = s?.freshlingsPaired ?? 0;
        const mentorsAccepted = s?.mentorsAccepted ?? 0;
        const requiredMentors = Math.ceil((freshlingsWithNoTeam || 0) / 2);
        const mentorGap = Math.max(0, requiredMentors - mentorsAccepted);
        const pairingPct =
          activeFreshlings > 0 ? Math.round((freshlingsPaired / activeFreshlings) * 100) : 0;
        const kpiCapacitySuccess = capacityPct != null && capacityPct >= 90;
        const kpiPairingSuccess = pairingPct >= 90;
        const daysUntil = getDaysUntilEvent(activeEvent);
        const signupActivity = signupActivityByEventId[activeEvent.id] || { last24All: 0, last7ByCategory: {} };
        const last24All = signupActivity.last24All || 0;
        const barChartData = activeRow
          ? DAY_COLS.map((col, i) => ({ day: DAY_HEADERS[i], count: Number(activeRow[col]) || 0 }))
          : [];
        const visibleIssues = showAllIssues ? scopedIssues : scopedIssues.slice(0, 4);
        const hasMoreIssues = scopedIssues.length > 4;

        return (
          <>
            <section className="admin-dashboard-section admin-dashboard-exec">
              {showTshirtDashboardWarning && (
                <div className="admin-dashboard-tshirt-banner" role="alert">
                  <span className="admin-dashboard-tshirt-banner-text">
                    <strong>T-Shirts:</strong> {tshirtFirestoreUnallocatedCount} roster{' '}
                    {tshirtFirestoreUnallocatedCount !== 1 ? 'members' : 'member'} have{' '}
                    <strong>no allocation saved yet</strong> (not the same as &quot;None / no shirt&quot;).
                    Inventory is recorded for this event — open T-Shirts and set sizes (they save as you
                    change them).
                  </span>
                  <button
                    type="button"
                    className="admin-dashboard-tshirt-banner-btn"
                    onClick={() => navigate('/admin/tshirts')}
                  >
                    Open T-Shirts
                  </button>
                </div>
              )}
              <div className="admin-dashboard-exec-top">
                <div>
                  <h2>{activeEvent.title}</h2>
                  <p className="admin-dashboard-muted">{activeEvent.date}</p>
                </div>
                <div className="admin-dashboard-event-actions">
                  <button
                    type="button"
                    className="btn-fm-primary"
                    onClick={() => setImportAllConfirmOpen(true)}
                    disabled={
                      !!importingAllEventId ||
                      (activeEvent.registrationImportSource === 'fury_api'
                        ? !(activeEvent.furyRegistrationEventId || '').trim()
                        : !activeEvent.freshlingsSpreadsheetId)
                    }
                  >
                    {importingAllEventId === activeEvent.id ? 'IMPORTING…' : 'IMPORT ALL'}
                  </button>
                  {importAllStatus?.eventId === activeEvent.id && (
                    <span
                      className={`admin-dashboard-import-feedback admin-dashboard-import-feedback--${importAllStatus.type}`}
                      role={importAllStatus.type === 'error' ? 'alert' : 'status'}
                      aria-live="polite"
                    >
                      {importAllStatus.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="admin-dashboard-kpi-grid">
                <div className="admin-dashboard-kpi">
                  <span className="admin-dashboard-kpi-label">Registrations</span>
                  <span className="admin-dashboard-kpi-value">{activeFreshlings ?? '—'}</span>
                </div>
                <div className="admin-dashboard-kpi">
                  <span className="admin-dashboard-kpi-label">Last 24h</span>
                  <span className="admin-dashboard-kpi-value">+{last24All}</span>
                </div>
                <div
                  className={`admin-dashboard-kpi${kpiCapacitySuccess ? ' admin-dashboard-kpi--success' : ''}`}
                >
                  <span className="admin-dashboard-kpi-label">Capacity</span>
                  <span className="admin-dashboard-kpi-value">{capacityPct != null ? `${capacityPct}%` : '—'}</span>
                </div>
                <div
                  className={`admin-dashboard-kpi${mentorGap > 0 ? ' admin-dashboard-kpi--warning' : ''}`}
                >
                  <span className="admin-dashboard-kpi-label">Mentor gap</span>
                  <span className="admin-dashboard-kpi-value">{mentorGap}</span>
                </div>
                <div
                  className={`admin-dashboard-kpi${kpiPairingSuccess ? ' admin-dashboard-kpi--success' : ''}`}
                >
                  <span className="admin-dashboard-kpi-label">Pairing complete</span>
                  <span className="admin-dashboard-kpi-value">{pairingPct}%</span>
                </div>
              </div>
              {daysUntil != null && (
                <div className="admin-dashboard-countdown">
                  {daysUntil > 0
                    ? `${daysUntil} DAY${daysUntil === 1 ? '' : 'S'} UNTIL EVENT`
                    : daysUntil === 0
                      ? 'EVENT IS TODAY'
                      : 'EVENT HAS PASSED'}
                </div>
              )}
            </section>

            <section className="admin-dashboard-section admin-dashboard-main-grid">
              <article className="admin-dashboard-events">
                <div className="admin-dashboard-total-row">
                  <div className="admin-dashboard-total-freshlings">
                    <span className="admin-dashboard-total-freshlings-number">{activeFreshlings != null ? activeFreshlings : '—'}</span>
                    <span className="admin-dashboard-total-freshlings-label">ACTIVE REGISTRATIONS</span>
                  </div>
                  {activeRow && barChartData.length > 0 && (
                    <div className="admin-dashboard-mini-chart">
                      <div className="admin-dashboard-mini-chart-title">BY DAY</div>
                      <div className="admin-dashboard-mini-chart-wrap">
                        <ResponsiveContainer width={180} height={72}>
                          <BarChart data={barChartData} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                            <XAxis dataKey="day" tick={{ fontSize: 10 }} stroke="#64748b" />
                            <YAxis tick={{ fontSize: 9 }} stroke="#64748b" allowDecimals={false} />
                            <Tooltip formatter={(v) => [v, 'Registrations']} />
                            <Bar dataKey="count" fill="#f07814" radius={[3, 3, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}
                </div>

                <div className="admin-dashboard-event-metrics">
                  <div className="admin-dashboard-event-metric admin-dashboard-event-metric--team">
                    <FreshlingsPie waiting={freshlingsWithNoTeam} paired={freshlingsPaired} />
                    <div className="admin-dashboard-event-metric-values">
                      <span className="admin-dashboard-metric-pill"><span className="admin-dashboard-metric-pill-value">{freshlingsWithNoTeam}</span><span className="admin-dashboard-metric-pill-label">No team</span></span>
                      <span className="admin-dashboard-metric-pill"><span className="admin-dashboard-metric-pill-value">{freshlingsPaired}</span><span className="admin-dashboard-metric-pill-label">Paired</span></span>
                      <span className="admin-dashboard-metric-pill"><span className="admin-dashboard-metric-pill-value">{teams.length}</span><span className="admin-dashboard-metric-pill-label">Teams</span></span>
                    </div>
                  </div>
                </div>

                {subEventRows.length > 0 && (
                  <div className="admin-dashboard-subevents">
                    <div className="admin-dashboard-card-row">
                      <div className="admin-dashboard-subevents-title">Sub-events</div>
                      <button type="button" className="admin-dashboard-btn-link" onClick={() => setShowSubEventDetails((prev) => !prev)}>
                        {showSubEventDetails ? 'COLLAPSE' : 'EXPAND'}
                      </button>
                    </div>
                    <div className="admin-dashboard-subevents-grid">
                      {(showSubEventDetails ? subEventRows : subEventRows.slice(0, 6)).map((row, i) => {
                        const total = Number(row.total) || 0;
                        const recent7 = (signupActivity.last7ByCategory && signupActivity.last7ByCategory[row.label]) || 0;
                        return (
                          <div key={i} className="admin-dashboard-subevent-card">
                            <div className="admin-dashboard-subevent-label">{row.label}</div>
                            <div className="admin-dashboard-subevent-value-wrap">
                              <span className="admin-dashboard-subevent-value">{total}{recent7 > 0 && <span className="admin-dashboard-subevent-delta">(+{recent7})</span>}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </article>

              <article className="admin-dashboard-issues">
                <div className="admin-dashboard-card-row">
                  <h2 className="admin-dashboard-section-heading">Issues</h2>
                  <label className="admin-dashboard-issues-toggle-all-events">
                    <input
                      type="checkbox"
                      checked={showAllIssueEvents}
                      onChange={(event) => {
                        setShowAllIssueEvents(event.target.checked);
                        setShowAllIssues(false);
                      }}
                      aria-label="Show issues from all events for cross-year comparison"
                    />
                    Show all events
                  </label>
                  {hasMoreIssues && (
                    <button type="button" className="admin-dashboard-btn-link" onClick={() => setShowAllIssues((prev) => !prev)}>
                      {showAllIssues ? 'SHOW LESS' : `VIEW ALL (${scopedIssues.length})`}
                    </button>
                  )}
                </div>
                <div className="admin-dashboard-severity-strip">
                  <div className="admin-dashboard-severity-tile admin-dashboard-severity-tile--total">
                    <span>{issueSummary.total}</span>
                    <small>Total</small>
                  </div>
                  <div className="admin-dashboard-severity-tile admin-dashboard-severity-tile--warning">
                    <span>{issueSummary.warning}</span>
                    <small>Warnings</small>
                  </div>
                  <div className="admin-dashboard-severity-tile admin-dashboard-severity-tile--info">
                    <span>{issueSummary.info}</span>
                    <small>Info</small>
                  </div>
                </div>
                {loading ? <p className="admin-dashboard-muted">Loading…</p> : visibleIssues.length === 0 ? (
                  <div className="admin-dashboard-empty-state"><span className="admin-dashboard-empty-icon">✓</span><p>No issues right now.</p></div>
                ) : (
                  <div className="admin-dashboard-issues-list">
                    {visibleIssues.map((issue) => {
                      const ack = issue.eventId ? (acknowledgments[issue.eventId] || {})[issue.alertType] : null;
                      return <AlertItem key={issue.id} issue={issue} ack={ack} onMarkOk={handleMarkAlertOk} onMarkEntityOk={handleMarkAlertEntityOk} navigate={navigate} />;
                    })}
                  </div>
                )}
              </article>
            </section>
          </>
        );
      })()}

      {activeEvent && registrationFillChart && (
        <section className="admin-dashboard-section admin-dashboard-fill-section">
          <h2 className="admin-dashboard-section-heading">Registration fill (gross signups)</h2>
          {!registrationFillChart.showSection ? (
            <p className="admin-dashboard-muted">No curve data yet.</p>
          ) : (
            <>
              {historicFillCurveEvents.length > 0 && (
                <div className="admin-dashboard-fill-toggles-bar">
                  <div className="admin-dashboard-fill-bulk">
                    <button
                      type="button"
                      className="admin-dashboard-fill-bulk-btn"
                      onClick={() =>
                        setFillHistVisibleById((prev) => {
                          const next = { ...prev };
                          historicFillCurveEvents.forEach((ev) => {
                            next[ev.id] = true;
                          });
                          return next;
                        })
                      }
                    >
                      Select all years
                    </button>
                    <button
                      type="button"
                      className="admin-dashboard-fill-bulk-btn"
                      onClick={() =>
                        setFillHistVisibleById((prev) => {
                          const next = { ...prev };
                          historicFillCurveEvents.forEach((ev) => {
                            next[ev.id] = false;
                          });
                          return next;
                        })
                      }
                    >
                      Deselect all years
                    </button>
                  </div>
                  <div className="admin-dashboard-fill-toggles" aria-label="Years on chart">
                    {historicFillCurveEvents.map((e) => (
                      <label key={e.id} className="admin-dashboard-fill-toggle">
                        <input
                          type="checkbox"
                          checked={fillHistVisibleById[e.id] !== false}
                          onChange={(ev) =>
                            setFillHistVisibleById((prev) => ({ ...prev, [e.id]: ev.target.checked }))
                          }
                        />
                        <span
                          className="admin-dashboard-fill-swatch"
                          style={{ background: historicFillStrokeById.get(e.id) || '#64748b' }}
                          title="Line color on chart"
                          aria-hidden
                        />
                        <span>{fillCurveDisplayYear(e)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              {registrationFillChart.paceLabel && (
                <div className="admin-dashboard-fill-pace" role="status">
                  <strong>{registrationFillChart.paceLabel}</strong>
                  {registrationFillChart.paceDelta != null && (
                    <span className="admin-dashboard-fill-pace-delta">
                      {' '}
                      ({registrationFillChart.paceDelta >= 0 ? '+' : ''}
                      {registrationFillChart.paceDelta} signups vs median of selected years)
                    </span>
                  )}
                </div>
              )}
              <div className="admin-dashboard-fill-chart-wrap">
                <ResponsiveContainer width="100%" height={340}>
                  <LineChart
                    data={registrationFillChart.chartRows}
                    margin={{ top: 16, right: 20, left: 6, bottom: 52 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 6"
                      stroke="rgba(248, 250, 252, 0.08)"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="d"
                      type="number"
                      domain={['dataMin', 'dataMax']}
                      reversed
                      tick={(props) => (
                        <RegistrationFillXAxisTick
                          {...props}
                          eventDay={getEventDate(activeEvent)}
                          dark
                        />
                      )}
                      tickMargin={4}
                      axisLine={{ stroke: '#475569' }}
                      tickLine={{ stroke: '#475569' }}
                    />
                    <YAxis
                      domain={[0, 'dataMax']}
                      width={44}
                      allowDecimals={false}
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      axisLine={{ stroke: '#475569' }}
                      tickLine={{ stroke: '#475569' }}
                      tickFormatter={(v) => (Number.isFinite(v) ? String(Math.round(v)) : '')}
                      label={{
                        value: 'Gross signups',
                        angle: -90,
                        position: 'insideLeft',
                        fill: '#cbd5e1',
                        fontSize: 11,
                        offset: 8,
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#1e293b',
                        border: '1px solid #f07814',
                        borderRadius: 8,
                        color: '#f8fafc',
                      }}
                      labelStyle={{ color: '#94a3b8' }}
                      formatter={(v) =>
                        v == null || Number.isNaN(Number(v)) ? '—' : `${Math.round(Number(v))} signups`
                      }
                      labelFormatter={(d) => {
                        const ed = getEventDate(activeEvent);
                        const cd = calendarDayBeforeEventSaturday(ed, d);
                        const dateStr =
                          cd != null ? `${cd.getMonth() + 1}/${cd.getDate()}` : '';
                        return dateStr ? `${dateStr} · ${d}d before event` : `${d}d before event`;
                      }}
                    />
                    <Legend wrapperStyle={{ paddingTop: 8, color: '#e2e8f0' }} />
                    {registrationFillChart.historicsShown.map((e) => (
                      <Line
                        key={e.id}
                        type="linear"
                        dataKey={`h_${e.id}`}
                        name={fillCurveDisplayYear(e)}
                        stroke={historicFillStrokeById.get(e.id) || '#64748b'}
                        strokeWidth={1.85}
                        strokeOpacity={0.88}
                        dot={false}
                        connectNulls
                      />
                    ))}
                    {registrationFillChart.historicsShown.length > 0 && (
                      <Line
                        type="linear"
                        dataKey="medianCount"
                        name="Median (selected)"
                        stroke="#e2e8f0"
                        strokeWidth={2.5}
                        strokeDasharray="6 5"
                        dot={false}
                        connectNulls
                      />
                    )}
                    <Line
                      type="linear"
                      dataKey="currentCount"
                      name="This edition"
                      stroke="#ffc078"
                      strokeWidth={5.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        filter:
                          'drop-shadow(0 0 2px rgba(255, 255, 255, 0.7)) drop-shadow(0 0 10px rgba(240, 120, 20, 0.95)) drop-shadow(0 0 18px rgba(234, 88, 12, 0.55))',
                      }}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </section>
      )}

      {activeEvent && (
        <section className="admin-dashboard-section admin-dashboard-secondary-grid">
          {dzStats.withDz > 0 && (
            <section className="admin-dashboard-dz">
              <h2 className="admin-dashboard-section-heading">Where Are Freshlings From?</h2>
              <div className="admin-dashboard-dz-stats">
                <div className="admin-dashboard-dz-callout admin-dashboard-dz-callout--ca">
                  <div className="admin-dashboard-dz-callout-value">{dzStats.caCount}</div>
                  <div className="admin-dashboard-dz-callout-label">from California</div>
                </div>
                <div className="admin-dashboard-dz-callout-pair">
                  <div className="admin-dashboard-dz-callout">
                    <div className="admin-dashboard-dz-callout-value">{dzStats.nonCaUs}</div>
                    <div className="admin-dashboard-dz-callout-label">Other US states</div>
                  </div>
                  {dzStats.intl > 0 && (
                    <div className="admin-dashboard-dz-callout">
                      <div className="admin-dashboard-dz-callout-value">{dzStats.intl}</div>
                      <div className="admin-dashboard-dz-callout-label">International</div>
                    </div>
                  )}
                </div>
                <button type="button" className="admin-dashboard-btn-link" onClick={() => setShowGeoDetails((prev) => !prev)}>
                  {showGeoDetails ? 'HIDE MAP DETAILS' : 'OPEN MAP DETAILS'}
                </button>
                {showGeoDetails && (
                  <div className="admin-dashboard-dz-map">
                    <ComposableMap projection="geoAlbersUsa" projectionConfig={{ scale: 700 }} width={700} height={360} style={{ width: '100%', height: 'auto' }}>
                      <Geographies geography={US_GEO_URL}>
                        {({ geographies }) =>
                          geographies.map((geo) => {
                            const abbr = FIPS_TO_STATE[geo.id];
                            const count = abbr ? (dzStats.stateByAbbr[abbr] || 0) : 0;
                            const intensity = count > 0 ? Math.max(0.15, count / dzStats.maxStateCount) : 0;
                            return (
                              <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                fill={count > 0 ? `rgba(240, 120, 20, ${intensity})` : '#e8e8e8'}
                                stroke="#fff"
                                strokeWidth={0.5}
                                style={{ default: { outline: 'none' }, hover: { outline: 'none' }, pressed: { outline: 'none' } }}
                              />
                            );
                          })
                        }
                      </Geographies>
                    </ComposableMap>
                    {dzStats.stateRows.length > 0 && (
                      <table className="admin-dashboard-dz-table">
                        <thead><tr><th>State</th><th>Count</th><th>%</th></tr></thead>
                        <tbody>
                          {dzStats.stateRows.map((r) => (
                            <tr key={r.label} className={r.label === 'CA' ? 'admin-dashboard-dz-row-ca' : ''}>
                              <td>{r.label}</td>
                              <td className="admin-dashboard-dz-num">{r.count}</td>
                              <td className="admin-dashboard-dz-num">{r.pct}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="admin-dashboard-btn-outline"
                  onClick={() => navigate(`/admin/flyer-management?eventId=${encodeURIComponent(activeEvent.id)}`)}
                >
                  OPEN FLYER MANAGEMENT
                </button>
              </div>
            </section>
          )}
        </section>
      )}
    </div>
  );
}

export default AdminDashboard;
